import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { api } from "../lib/api";

/**
 * Single source of truth for the user's subscription state.
 *
 *   status          : "trialing" | "active" | "free"
 *   is_pro          : boolean — within trial OR within paid period
 *   in_trial        : boolean
 *   in_paid         : boolean
 *   days_remaining  : days until access ends (0 once expired)
 *   trial_ends_at   : ISO
 *   pro_until       : ISO | null
 *   access_end      : ISO | null — whichever is later (trial OR paid)
 *   plan            : { id, amount, currency, period_days, name, description }
 *   refresh()       : re-fetch from server (call after checkout completes)
 */
const BillingContext = createContext(null);

export function BillingProvider({ children }) {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setSub(null);
      return null;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/billing/status");
      setSub(data);
      return data;
    } catch {
      setSub(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <BillingContext.Provider value={{ sub, loading, refresh }}>
      {children}
    </BillingContext.Provider>
  );
}

export const useBilling = () => {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used inside <BillingProvider>");
  return ctx;
};
